import {defineMessages, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {getLocaleCode, getSeoActiveLocales} from 'modules/meta/selectors'
import {getHotelResult} from 'modules/searchApi/selectors'
import {useGetHotelMetadataQuery} from 'modules/seoApi'
import {getHotelMetadataParams} from 'modules/seoApi/selectors'
import {useParams} from 'next/navigation'
import {buildHreflangLinks, SeoHrefLangLinks} from 'seo/hreflang'
import {imageProvider} from 'utils/imageProvider'
import {buildSeoApPath} from 'utils/server/apSeo/getSeoAPPath'
import {IndexMetadata, SeoMetadata} from 'utils/server/searchSeo/getSeoMetadata'
import {convertHotelSlugToHotelId} from 'views/accommodation/convertHotelSlugToHotelId'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'

const messages = defineMessages({
  apDescription: {
    id: 'seoMeta.apMetaDescription',
    defaultMessage:
      'Book your stay at {hotelName} like an insider. We show real hotel prices across 100+ sites, compare options transparently, and help travelers book with confidence.'
  }
})

export interface OpenGraphMetadata {
  title: string
  imageUrl: string
  /** Only known for the imgproxy crop; the logo fallback is served as-is. */
  imageSize?: [width: number, height: number]
}

export type SeoAPMetadata = SeoMetadata &
  IndexMetadata &
  SeoHrefLangLinks & {openGraph: OpenGraphMetadata}

// Facebook and Twitter render large previews at 1.91:1 and want at least 1200px wide.
const OPEN_GRAPH_IMAGE_SIZE: [width: number, height: number] = [1200, 630]

const MAX_OPEN_GRAPH_TITLE_LENGTH = 70

const TITLE_SEPARATOR = ' | '

/**
 * The page title carries a brand suffix (`... | Vio.com`) that link previews
 * repeat via `og:site_name`, so it is dropped here. Only a segment matching
 * the brand is dropped, so a title ending in a place name keeps it. The hotel
 * name is never shortened: an over-long title falls back to the plain name.
 */
const getOpenGraphTitle = (
  seoTitle: string | undefined,
  hotelName: string,
  brandNames: string[]
) => {
  if (!seoTitle) return hotelName

  const separatorIndex = seoTitle.lastIndexOf(TITLE_SEPARATOR)
  const suffix = seoTitle
    .slice(separatorIndex + TITLE_SEPARATOR.length)
    .toLowerCase()
  const hasBrandSuffix =
    separatorIndex !== -1 &&
    brandNames.some(name => name.toLowerCase() === suffix)

  const title = hasBrandSuffix ? seoTitle.slice(0, separatorIndex) : seoTitle

  return title.length > MAX_OPEN_GRAPH_TITLE_LENGTH ? hotelName : title
}

// Cropped and upscaled to a fixed size so the declared og:image dimensions match the served image
const getOpenGraphImageUrl = (imageUrl: string) => {
  const [width, height] = OPEN_GRAPH_IMAGE_SIZE

  return imageProvider(imageUrl, OPEN_GRAPH_IMAGE_SIZE, {
    rs: `fill:${width}:${height}:1:0`
  })
}

export const useSeoAPMetadata = (): SeoAPMetadata | null => {
  const params = useParams()
  const {formatMessage} = useIntl()
  const {brand, brandPageTitleTagLineMessage} = useBrand()
  const {search} = useBrandCapabilities()
  const locale = useSelector(getLocaleCode)
  const seoActiveLocales = useSelector(getSeoActiveLocales)
  const hotelId = convertHotelSlugToHotelId(params.hotelSlug as string)
  const {data: hotel} = useSelector(state => getHotelResult(state, hotelId))

  const isAP = !!hotelId

  const metadataParams = useSelector(getHotelMetadataParams)
  const {metadata} = useGetHotelMetadataQuery(metadataParams, {
    selectFromResult: result => ({metadata: result?.data}),
    // seo-db titles carry the Vio.com suffix, so brands without AP SEO keep their own
    skip: !isAP || !search.hasIndexableApSeo
  })

  if (!isAP || !hotel) return null

  const baseUrl = brand.siteAddresses.address
  const path = buildSeoApPath({hotelId, locale})

  const tagline = formatMessage(brandPageTitleTagLineMessage)
  const title =
    metadata?.title || `${hotel.hotelName} | ${tagline} | ${brand.name}`

  const description =
    metadata?.description ||
    formatMessage(messages.apDescription, {hotelName: hotel.hotelName})

  const isIndexableLocale = seoActiveLocales.includes(locale)
  const hreflangLinks = isIndexableLocale
    ? buildHreflangLinks(baseUrl, seoActiveLocales, code =>
        buildSeoApPath({hotelId, locale: code})
      )
    : []

  // Deindexed pages keep `follow`, so a partner page, a non-eligible locale or
  // a property deleted by Content still passes link equity on. A missing
  // response (still loading, or the endpoint failed) must not deindex.
  const isIndexable =
    search.hasIndexableApSeo && isIndexableLocale && !hotel.isDeleted

  const robots = isIndexable
    ? (metadata?.robots ?? 'index, follow')
    : 'noindex, follow'

  // Same source as the gallery hero, so the preview shows the image the page opens with
  const heroImageUrl = hotel.images?.[0]?.href ?? hotel.imageURIs?.[0]

  // seo-db titles end in the site name (`Vio.com`) rather than the brand name
  const brandNames = [
    brand.name,
    new URL(baseUrl).hostname.replace(/^www\./, '')
  ]
  const openGraphTitle = getOpenGraphTitle(
    metadata?.title,
    hotel.hotelName,
    brandNames
  )

  const openGraph: OpenGraphMetadata = heroImageUrl
    ? {
        title: openGraphTitle,
        imageUrl: getOpenGraphImageUrl(heroImageUrl),
        imageSize: OPEN_GRAPH_IMAGE_SIZE
      }
    : {
        title: openGraphTitle,
        // Scrapers need an absolute URL, so the logo is resolved against the site address
        imageUrl: new URL(`/static/brands/${brand.code}.png`, baseUrl).href
      }

  return {
    robots,
    canonical: `${baseUrl}${path}`,
    title,
    description,
    heading: metadata?.heading || '',
    hreflangLinks,
    openGraph
  }
}
